import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Languages, LogOut, Menu, UserRound } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearAuthSession } from "../api/fetchApi";

const titles = {
  ar: {
    admin: "نظرة عامة",
    session: "ربط واتساب",
    chat: "سجل الرسائل",
    "auto-reply": "الردود الآلية",
    profile: "الملف الشخصي",
    "send-message": "إرسال رسالة",
    client: "العملاء",
    groups: "المجموعات",
    users: "إدارة المستخدمين",
    "api-docs": "دليل API",
  },
  en: {
    admin: "Overview",
    session: "WhatsApp connection",
    chat: "Message history",
    "auto-reply": "Auto replies",
    profile: "Profile",
    "send-message": "Send message",
    client: "Customers",
    groups: "Groups",
    users: "User management",
    "api-docs": "API guide",
  },
};

function pageTitle(pathname, lang) {
  const segment = pathname.split("/").filter(Boolean).pop() || "admin";
  return titles[lang][segment] || titles[lang].admin;
}

export default function Header({ onSidebarToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [lang, setLang] = useState(localStorage.getItem("lang") || "ar");
  const [accountOpen, setAccountOpen] = useState(false);
  const isArabic = lang === "ar";
  const username = localStorage.getItem("user") || (isArabic ? "مستخدم" : "User");

  useEffect(() => {
    const closeMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setAccountOpen(false);
    };
    document.addEventListener("pointerdown", closeMenu);
    return () => document.removeEventListener("pointerdown", closeMenu);
  }, []);

  const handleLogout = () => {
    clearAuthSession();
    navigate("/auth/login", { replace: true });
  };

  const handleLanguage = (event) => {
    const nextLanguage = event.target.value;
    localStorage.setItem("lang", nextLanguage);
    setLang(nextLanguage);
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-20 flex h-[72px] items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
      <div className="flex min-w-0 items-center gap-3">
        <button type="button" onClick={onSidebarToggle} className="icon-button" aria-label={isArabic ? "فتح القائمة" : "Open navigation"}>
          <Menu size={21} />
        </button>
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">
            {isArabic ? "لوحة التحكم" : "Dashboard"}
          </p>
          <h1 className="truncate text-lg font-semibold tracking-tight text-slate-950 sm:text-xl">
            {pageTitle(location.pathname, lang)}
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <label className="language-select">
          <Languages size={17} aria-hidden="true" />
          <span className="sr-only">{isArabic ? "اللغة" : "Language"}</span>
          <select value={lang} onChange={handleLanguage} aria-label={isArabic ? "تغيير اللغة" : "Change language"}>
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </select>
        </label>

        <div className="relative" ref={menuRef}>
          <button type="button" className="account-button" onClick={() => setAccountOpen((open) => !open)} aria-expanded={accountOpen}>
            <span className="account-avatar">{username.slice(0, 1).toUpperCase()}</span>
            <span className="hidden max-w-28 truncate text-sm font-semibold sm:block">{username}</span>
            <ChevronDown size={15} className={`hidden transition-transform sm:block ${accountOpen ? "rotate-180" : ""}`} />
          </button>

          {accountOpen && (
            <div className={`account-menu ${isArabic ? "left-0" : "right-0"}`}>
              <div className="border-b border-slate-100 px-3 py-2.5">
                <span className="block text-xs text-slate-500">{isArabic ? "تم الدخول باسم" : "Signed in as"}</span>
                <strong className="block truncate text-sm">{username}</strong>
              </div>
              <Link to="/admin/profile" onClick={() => setAccountOpen(false)}><UserRound size={17} />{isArabic ? "الملف الشخصي" : "Profile"}</Link>
              <button type="button" onClick={handleLogout}><LogOut size={17} />{isArabic ? "تسجيل الخروج" : "Sign out"}</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
